import React, { useState } from 'react';
import { Modal, Button, Card, Table, Form, Badge, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { UploadCloud, FileText, CheckCircle, AlertTriangle, ShieldCheck, DollarSign, Calculator, UserPlus, Trash2, PlusCircle } from 'lucide-react';
import apiClient from '../api';

const PayrollProcessorModal = ({ show, onHide, onTransactionsCreated }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isParsing, setIsParsing] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Estado para formulario de trabajador manual (Sin PDF)
    const [showAddManualForm, setShowAddManualForm] = useState(false);
    const [manualForm, setManualForm] = useState({
        employee_name: '',
        employee_rut: '',
        total_liquid: '',
        overtime_amount: '',
        viaticos_amount: ''
    });

    // Manejar selección de archivos PDF
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
        setSelectedFiles(files);
        setErrorMessage(null);
        setPreviewData(null);
    };

    // Subir y analizar PDFs
    const handleAnalyzePdfs = async () => {
        if (!selectedFiles || selectedFiles.length === 0) {
            setErrorMessage("Por favor selecciona al menos un archivo PDF de liquidación.");
            return;
        }

        setIsParsing(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('files', file);
        });

        try {
            const response = await apiClient.post('/api/payroll/parse-slips', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPreviewData(response.data);
        } catch (err) {
            console.error("Error al procesar PDFs de liquidación:", err);
            const msg = err.response?.data?.detail || "Error al analizar los archivos PDF. Intenta nuevamente.";
            setErrorMessage(msg);
        } finally {
            setIsParsing(false);
        }
    };

    // Recalcular montos consolidados y transacciones propuestas al modificar trabajadores
    const recalculateData = (slips) => {
        const total_slips = slips.length;
        const total_liquid_sum = slips.reduce((acc, s) => acc + (parseFloat(s.total_liquid) || 0), 0);
        const total_overtime_sum = slips.reduce((acc, s) => acc + (parseFloat(s.overtime_amount) || 0), 0);
        const total_viaticos_sum = slips.reduce((acc, s) => acc + (parseFloat(s.viaticos_amount) || 0), 0);
        const net_liquid_sum = Math.max(0, total_liquid_sum - (total_overtime_sum + total_viaticos_sum));

        const currentTxs = previewData ? previewData.proposed_transactions : [];
        const periodLabel = previewData?.period_label || 'Mes Anterior';

        const descSueldo = currentTxs[0]?.description || `Remuneraciones ${periodLabel}`;
        const descHhee = currentTxs[1]?.description || `Horas Extras ${periodLabel}`;
        const descViaticos = currentTxs[2]?.description || `Viáticos ${periodLabel}`;

        const updatedTxs = [
            {
                key: "sueldo_liquido",
                title: "Sueldo Líquido Total",
                company: "Tmuevo",
                type: "Egreso",
                category: "Remuneraciones",
                subcategory: "Sueldo",
                amount: net_liquid_sum,
                description: descSueldo,
                notes: `Sueldo líquido acumulado de ${total_slips} trabajadores (menos HHEE y Viáticos).`
            },
            {
                key: "horas_extras",
                title: "Horas Extras (HHEE)",
                company: "Tmuevo",
                type: "Egreso",
                category: "Remuneraciones",
                subcategory: "HHEE",
                amount: total_overtime_sum,
                description: descHhee,
                notes: `Total de horas extras correspondientes a ${total_slips} trabajadores.`
            },
            {
                key: "viaticos",
                title: "Viáticos",
                company: "Tmuevo",
                type: "Egreso",
                category: "Remuneraciones",
                subcategory: "Viáticos",
                amount: total_viaticos_sum,
                description: descViaticos,
                notes: `Total de viáticos y asignaciones de ${total_slips} trabajadores.`
            }
        ];

        setPreviewData({
            ...previewData,
            total_slips,
            totals: {
                total_liquid: total_liquid_sum,
                total_overtime: total_overtime_sum,
                total_viaticos: total_viaticos_sum,
                net_liquid: net_liquid_sum
            },
            slips_detail: slips.map(s => ({
                ...s,
                net_liquid: Math.max(0, (parseFloat(s.total_liquid) || 0) - ((parseFloat(s.overtime_amount) || 0) + (parseFloat(s.viaticos_amount) || 0)))
            })),
            proposed_transactions: updatedTxs
        });
    };

    // Agregar trabajador manual (Sin PDF)
    const handleAddManualWorker = (e) => {
        e.preventDefault();
        if (!manualForm.employee_name.trim()) {
            setErrorMessage("Por favor ingresa el nombre del trabajador.");
            return;
        }

        const newWorker = {
            filename: "Ingreso Manual (Sin PDF)",
            employee_name: manualForm.employee_name.trim(),
            employee_rut: manualForm.employee_rut.trim() || "S/RUT",
            total_liquid: parseFloat(manualForm.total_liquid) || 0,
            overtime_amount: parseFloat(manualForm.overtime_amount) || 0,
            viaticos_amount: parseFloat(manualForm.viaticos_amount) || 0,
            is_manual: true
        };

        const currentSlips = previewData ? (previewData.slips_detail || []) : [];
        recalculateData([...currentSlips, newWorker]);

        setManualForm({
            employee_name: '',
            employee_rut: '',
            total_liquid: '',
            overtime_amount: '',
            viaticos_amount: ''
        });
        setShowAddManualForm(false);
        setErrorMessage(null);
    };

    // Eliminar trabajador de la lista
    const handleRemoveWorker = (index) => {
        if (!previewData || !previewData.slips_detail) return;
        const updatedSlips = previewData.slips_detail.filter((_, i) => i !== index);
        recalculateData(updatedSlips);
    };

    // Cambiar descripción u otros campos en la previsualización
    const handleTransactionChange = (index, field, value) => {
        if (!previewData) return;
        const updatedTxs = [...previewData.proposed_transactions];
        updatedTxs[index][field] = value;
        setPreviewData({ ...previewData, proposed_transactions: updatedTxs });
    };

    // Confirmar e insertar transacciones
    const handleConfirmTransactions = async () => {
        if (!previewData || !previewData.proposed_transactions) return;

        setIsConfirming(true);
        setErrorMessage(null);

        try {
            const response = await apiClient.post('/api/payroll/confirm-transactions', {
                transactions: previewData.proposed_transactions
            });

            setSuccessMessage(response.data.message || "Transacciones generadas con éxito en el Flujo de Caja.");
            if (onTransactionsCreated) {
                onTransactionsCreated();
            }
            setTimeout(() => {
                handleReset();
                onHide();
            }, 2000);
        } catch (err) {
            console.error("Error al generar transacciones de remuneraciones:", err);
            const msg = err.response?.data?.detail || "No se pudieron insertar las transacciones.";
            setErrorMessage(msg);
        } finally {
            setIsConfirming(false);
        }
    };

    const handleReset = () => {
        setSelectedFiles([]);
        setPreviewData(null);
        setErrorMessage(null);
        setSuccessMessage(null);
        setIsParsing(false);
        setIsConfirming(false);
        setShowAddManualForm(false);
        setManualForm({
            employee_name: '',
            employee_rut: '',
            total_liquid: '',
            overtime_amount: '',
            viaticos_amount: ''
        });
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(val || 0);
    };

    return (
        <Modal show={show} onHide={() => { handleReset(); onHide(); }} size="lg" centered scrollable backdrop="static">
            <Modal.Header closeButton className="bg-dark text-white border-0 py-3">
                <Modal.Title className="h5 mb-0 fw-bold d-flex align-items-center gap-2">
                    <ShieldCheck className="text-warning" size={24} />
                    PROCESADOR DE LIQUIDACIONES DE SUELDO (SENSIBLE)
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light p-4">
                {errorMessage && (
                    <Alert variant="danger" dismissible onClose={() => setErrorMessage(null)} className="d-flex align-items-center gap-2 mb-3">
                        <AlertTriangle size={20} />
                        <div>{errorMessage}</div>
                    </Alert>
                )}

                {successMessage && (
                    <Alert variant="success" className="d-flex align-items-center gap-2 fw-bold mb-3">
                        <CheckCircle size={20} />
                        <div>{successMessage}</div>
                    </Alert>
                )}

                {/* PASO 1: SELECCIÓN DE ARCHIVOS */}
                {!previewData && (
                    <div className="text-center py-4 bg-white rounded shadow-sm border p-4">
                        <UploadCloud size={56} className="text-primary mb-3 animate-pulse" />
                        <h5 className="fw-bold text-dark mb-2">Carga masiva de Liquidaciones en PDF</h5>
                        <p className="text-muted small max-w-md mx-auto mb-4">
                            Selecciona los PDFs de liquidaciones de sueldo. El sistema extraerá el Sueldo Líquido Neto, Horas Extras (HHEE) y Viáticos para desglosarlos automáticamente en el Flujo de Caja.
                        </p>

                        <Form.Group controlId="formFileMultiple" className="mb-4">
                            <Form.Control 
                                type="file" 
                                multiple 
                                accept=".pdf" 
                                onChange={handleFileChange}
                                disabled={isParsing}
                                className="w-75 mx-auto py-2 border-2"
                            />
                        </Form.Group>

                        {selectedFiles.length > 0 && (
                            <div className="mb-4">
                                <Badge bg="info" className="p-2 fs-6 mb-2">
                                    {selectedFiles.length} archivo(s) PDF seleccionado(s)
                                </Badge>
                                <div className="text-muted small max-h-32 overflow-auto w-75 mx-auto text-start bg-light p-2 rounded border">
                                    {selectedFiles.map((f, i) => (
                                        <div key={i} className="d-flex align-items-center gap-2 py-1">
                                            <FileText size={14} className="text-danger" />
                                            <span className="text-truncate">{f.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <Button 
                            variant="primary" 
                            size="lg" 
                            className="px-5 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                            onClick={handleAnalyzePdfs}
                            disabled={selectedFiles.length === 0 || isParsing}
                        >
                            {isParsing ? (
                                <>
                                    <Spinner animation="border" size="sm" />
                                    Analizando Liquidaciones...
                                </>
                            ) : (
                                <>
                                    <Calculator size={20} />
                                    Generar Previsualización
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {/* PASO 2: PREVISUALIZACIÓN DE DESGLOSE Y TRANSACCIONES PROPUESTAS */}
                {previewData && (
                    <div>
                        {/* RESUMEN EJECUTIVO */}
                        <Card className="border-0 shadow-sm mb-4 bg-dark text-white">
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0 text-warning d-flex align-items-center gap-2">
                                        <FileText size={18} />
                                        Resumen de Liquidaciones - {previewData.period_label}
                                    </h6>
                                    <Badge bg="success" className="px-3 py-2">
                                        {previewData.total_slips} Trabajadores procesados
                                    </Badge>
                                </div>
                                <Row className="g-2 text-center">
                                    <Col xs={3}>
                                        <div className="bg-secondary bg-opacity-25 p-2 rounded">
                                            <div className="small text-muted">Sueldo Líquido Neto</div>
                                            <div className="fw-bold text-success fs-6">{formatCurrency(previewData.totals.net_liquid)}</div>
                                        </div>
                                    </Col>
                                    <Col xs={3}>
                                        <div className="bg-secondary bg-opacity-25 p-2 rounded">
                                            <div className="small text-muted">Horas Extras (HHEE)</div>
                                            <div className="fw-bold text-warning fs-6">{formatCurrency(previewData.totals.total_overtime)}</div>
                                        </div>
                                    </Col>
                                    <Col xs={3}>
                                        <div className="bg-secondary bg-opacity-25 p-2 rounded">
                                            <div className="small text-muted">Viáticos / Asignaciones</div>
                                            <div className="fw-bold text-info fs-6">{formatCurrency(previewData.totals.total_viaticos)}</div>
                                        </div>
                                    </Col>
                                    <Col xs={3}>
                                        <div className="bg-warning bg-opacity-25 p-2 rounded border border-warning">
                                            <div className="small text-warning fw-bold">TOTAL DE TODO</div>
                                            <div className="fw-bold text-white fs-6">{formatCurrency(previewData.totals.total_liquid)}</div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* TARJETAS DE TRANSACCIONES PROPUESTAS */}
                        <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                            <DollarSign size={18} className="text-success" />
                            Transacciones Propuestas para Flujo de Caja (Empresa: Tmuevo)
                        </h6>

                        {previewData.proposed_transactions.map((tx, idx) => (
                            <Card key={idx} className="border shadow-sm mb-3 bg-white">
                                <Card.Body className="p-3">
                                    <Row className="align-items-center g-3">
                                        <Col md={3}>
                                            <div className="d-flex align-items-center gap-1 mb-1">
                                                <Badge bg={tx.key === 'sueldo_liquido' ? 'success' : tx.key === 'horas_extras' ? 'warning' : 'info'} className="flex-grow-1 py-1 fs-6">
                                                    {tx.title}
                                                </Badge>
                                                <Badge bg={tx.status === 'pendiente' ? 'secondary' : 'success'} className="py-1">
                                                    {tx.status === 'pendiente' ? 'Pendiente' : 'Completado'}
                                                </Badge>
                                            </div>
                                            <div className="small text-muted fw-semibold">Empresa: <strong className="text-dark">{tx.company}</strong></div>
                                            <div className="small text-muted">Categoría: {tx.category} - {tx.subcategory}</div>
                                            {tx.accrual_date && <div className="small text-primary fw-bold">Imputación EERR: {tx.accrual_date}</div>}
                                        </Col>
                                        <Col md={3} className="text-md-end">
                                            <div className="small text-muted">Monto Total</div>
                                            <div className="fs-5 fw-bold text-dark">{formatCurrency(tx.amount)}</div>
                                        </Col>
                                        <Col md={6}>
                                            <Row className="g-2">
                                                <Col md={8}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold text-muted mb-1">Descripción / Observación</Form.Label>
                                                        <Form.Control 
                                                            type="text" 
                                                            size="sm"
                                                            value={tx.description} 
                                                            onChange={(e) => handleTransactionChange(idx, 'description', e.target.value)}
                                                            className="fw-bold"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-bold text-muted mb-1">Estado Flujo</Form.Label>
                                                        <Form.Select 
                                                            size="sm" 
                                                            value={tx.status || 'completado'} 
                                                            onChange={(e) => handleTransactionChange(idx, 'status', e.target.value)}
                                                            className="fw-bold"
                                                        >
                                                            <option value="completado">Completado</option>
                                                            <option value="pendiente">Pendiente</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}

                        {/* BANNER DE TOTAL GENERAL DE DE TODO */}
                        <div className="p-3 bg-white rounded border border-warning shadow-sm mb-4 d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <DollarSign size={20} className="text-warning" />
                                    TOTAL GENERAL A REGISTRAR EN REMUNERACIONES (TMUEVO)
                                </h6>
                                <span className="small text-muted">Suma acumulada total de Sueldo Líquido Neto + Horas Extras + Viáticos</span>
                            </div>
                            <div className="fs-4 fw-bold text-success">
                                {formatCurrency(previewData.totals.total_liquid)}
                            </div>
                        </div>

                        {/* DETALLE INDIVIDUAL DE TRABAJADORES Y MANTENEDOR MANUAL */}
                        <div className="mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold text-dark mb-0">Detalle por Trabajador ({previewData.slips_detail.length})</h6>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="fw-bold d-flex align-items-center gap-1"
                                    onClick={() => setShowAddManualForm(!showAddManualForm)}
                                >
                                    <UserPlus size={16} />
                                    {showAddManualForm ? "Cancelar Registro" : "➕ Agregar Trabajador Sin Liquidación PDF"}
                                </Button>
                            </div>

                            {/* FORMULARIO DE INGRESO MANUAL */}
                            {showAddManualForm && (
                                <Card className="border border-primary shadow-sm mb-3 bg-white">
                                    <Card.Body className="p-3">
                                        <h6 className="fw-bold text-primary mb-3 d-flex align-items-center gap-2">
                                            <PlusCircle size={18} />
                                            Registrar Trabajador Manual (Sin PDF)
                                        </h6>
                                        <Form onSubmit={handleAddManualWorker}>
                                            <Row className="g-2 mb-2">
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted mb-1">Nombre Completo Trabajador</Form.Label>
                                                        <Form.Control 
                                                            type="text" 
                                                            size="sm" 
                                                            placeholder="Ej. Juan Pérez" 
                                                            value={manualForm.employee_name} 
                                                            onChange={(e) => setManualForm({...manualForm, employee_name: e.target.value})}
                                                            required 
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted mb-1">RUT Trabajador (Opcional)</Form.Label>
                                                        <Form.Control 
                                                            type="text" 
                                                            size="sm" 
                                                            placeholder="12.345.678-9" 
                                                            value={manualForm.employee_rut} 
                                                            onChange={(e) => setManualForm({...manualForm, employee_rut: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row className="g-2 mb-3">
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted mb-1">Total Líquido ($)</Form.Label>
                                                        <Form.Control 
                                                            type="number" 
                                                            size="sm" 
                                                            placeholder="0" 
                                                            value={manualForm.total_liquid} 
                                                            onChange={(e) => setManualForm({...manualForm, total_liquid: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted mb-1">Turno Extra / HHEE ($)</Form.Label>
                                                        <Form.Control 
                                                            type="number" 
                                                            size="sm" 
                                                            placeholder="0" 
                                                            value={manualForm.overtime_amount} 
                                                            onChange={(e) => setManualForm({...manualForm, overtime_amount: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label className="small fw-semibold text-muted mb-1">Viáticos / Asignaciones ($)</Form.Label>
                                                        <Form.Control 
                                                            type="number" 
                                                            size="sm" 
                                                            placeholder="0" 
                                                            value={manualForm.viaticos_amount} 
                                                            onChange={(e) => setManualForm({...manualForm, viaticos_amount: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <div className="d-flex justify-content-end gap-2">
                                                <Button variant="secondary" size="sm" onClick={() => setShowAddManualForm(false)}>
                                                    Cancelar
                                                </Button>
                                                <Button variant="primary" size="sm" type="submit" className="fw-bold">
                                                    Sumar a la Previsualización
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* TABLA DE TRABAJADORES */}
                            <div className="table-responsive bg-white rounded border shadow-sm max-h-60 overflow-auto">
                                <Table hover size="sm" className="mb-0 align-middle">
                                    <thead className="bg-light sticky-top">
                                        <tr>
                                            <th>Trabajador</th>
                                            <th>RUT</th>
                                            <th className="text-end">Total Líquido</th>
                                            <th className="text-end">HHEE</th>
                                            <th className="text-end">Viáticos</th>
                                            <th className="text-end text-success">Líquido Neto</th>
                                            <th className="text-center" style={{ width: '40px' }}>Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.slips_detail.map((slip, i) => (
                                            <tr key={i} className={slip.is_manual ? "bg-primary bg-opacity-10" : ""}>
                                                <td className="fw-semibold">
                                                    {slip.employee_name}
                                                    {slip.is_manual && <Badge bg="primary" className="ms-2 small">Manual</Badge>}
                                                </td>
                                                <td className="small text-muted">{slip.employee_rut}</td>
                                                <td className="text-end">{formatCurrency(slip.total_liquid)}</td>
                                                <td className="text-end text-warning">{formatCurrency(slip.overtime_amount)}</td>
                                                <td className="text-end text-info">{formatCurrency(slip.viaticos_amount)}</td>
                                                <td className="text-end fw-bold text-success">{formatCurrency(slip.net_liquid)}</td>
                                                <td className="text-center">
                                                    <Button 
                                                        variant="link" 
                                                        size="sm" 
                                                        className="text-danger p-0 border-0" 
                                                        title="Quitar trabajador"
                                                        onClick={() => handleRemoveWorker(i)}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-white border-top py-3">
                {previewData ? (
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <Button variant="outline-secondary" onClick={() => setPreviewData(null)}>
                            ← Volver a Cargar PDFs
                        </Button>
                        <Button 
                            variant="success" 
                            className="px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
                            onClick={handleConfirmTransactions}
                            disabled={isConfirming}
                        >
                            {isConfirming ? (
                                <>
                                    <Spinner animation="border" size="sm" />
                                    Generando Transacciones...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={18} />
                                    Confirmar e Insertar en Flujo de Caja
                                </>
                            )}
                        </Button>
                    </div>
                ) : (
                    <Button variant="secondary" onClick={() => { handleReset(); onHide(); }}>
                        Cancelar
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PayrollProcessorModal;
